<script setup lang="ts">
  import '@/assets/calendar-legal-os.css'
  import type { CalendarViewItem } from '@/composables/useCalendarMainView'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import type { CalendarViewId } from '@/constants/calendar/views'
  import { CalendarEventTypeEnum } from '@/types/generated'
  import { Calendar, ClipboardList, Clock, PanelLeft, Plus, RotateCcw } from 'lucide-vue-next'

  type CalendarComposerMode = 'TERMIN' | 'FRIST' | 'WIEDERVORLAGE' | 'AUFGABE'

  defineProps<{
    sidebarVisible: boolean
    hideSidebarLabel: string
    showSidebarLabel: string
    views: CalendarViewItem[]
    activeView: CalendarViewId
    headingDate: string
    headingYear: string
    prevLabel: string
    nextLabel: string
    todayLabel: string
    addEventLabel: string
    addTerminLabel: string
    addFristLabel: string
    addWiedervorlageLabel: string
    addAufgabeLabel: string
    hasCalendars: boolean
  }>()

  const emit = defineEmits<{
    'toggle-sidebar': []
    'set-view': [id: CalendarViewId]
    'go-prev': []
    'go-next': []
    'go-today': []
    'add-event': [type: CalendarComposerMode]
  }>()

  const modeItems: Array<{
    id: CalendarComposerMode
    labelKey: 'addTerminLabel' | 'addFristLabel' | 'addWiedervorlageLabel' | 'addAufgabeLabel'
    icon: typeof Calendar
    disabled: boolean
  }> = [
    {
      id: CalendarEventTypeEnum.TERMIN,
      labelKey: 'addTerminLabel',
      icon: Calendar,
      disabled: false
    },
    { id: CalendarEventTypeEnum.FRIST, labelKey: 'addFristLabel', icon: Clock, disabled: true },
    {
      id: CalendarEventTypeEnum.WIEDERVORLAGE,
      labelKey: 'addWiedervorlageLabel',
      icon: RotateCcw,
      disabled: true
    },
    {
      id: CalendarEventTypeEnum.AUFGABE,
      labelKey: 'addAufgabeLabel',
      icon: ClipboardList,
      disabled: true
    }
  ]
</script>

<template>
  <div
    class="calendar-main border-navbar-line bg-navbar shrink-0 border-b px-2 py-3 sm:px-5"
    data-cy="calendar-main-toolbar"
  >
    <div class="mb-3 grid grid-cols-3 items-center gap-2">
      <div class="flex items-center gap-x-1">
        <button
          type="button"
          class="size-7.5 text-muted-foreground-1 hover:bg-muted-hover hidden items-center justify-center rounded-md
            border border-transparent p-1.5 text-xs hover:text-foreground focus:text-foreground
            focus:outline-none lg:inline-flex"
          :aria-label="sidebarVisible ? hideSidebarLabel : showSidebarLabel"
          data-cy="calendar-toggle-sidebar"
          @click="emit('toggle-sidebar')"
        >
          <PanelLeft class="size-3.5 shrink-0" />
        </button>
      </div>

      <div class="flex justify-center">
        <div
          class="border-layer-line bg-layer inline-flex justify-center gap-x-1 space-x-1 rounded-md border p-0.5
            shadow-sm"
          role="tablist"
        >
          <div
            v-for="view in views"
            :key="view.id"
            class="before:-inset-s-1 before:border-layer-line relative flex before:absolute before:top-1/2 before:h-3
              before:-translate-y-1/2 before:border-s first:before:hidden"
          >
            <button
              type="button"
              role="tab"
              :aria-selected="activeView === view.id"
              :class="[
                'inline-flex items-center rounded-sm px-2 py-1 text-xs focus:outline-none',
                activeView === view.id
                  ? 'bg-surface text-foreground'
                  : 'text-muted-foreground-1 hover:text-foreground'
              ]"
              :data-cy="`calendar-view-${view.id}`"
              @click="emit('set-view', view.id)"
            >
              {{ view.label }}
            </button>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-end gap-2 text-end">
        <DropdownMenu v-if="hasCalendars">
          <DropdownMenuTrigger as-child>
            <button
              type="button"
              class="border-primary-line focus:bg-primary-focus inline-flex items-center justify-center gap-x-2
                rounded-lg border bg-primary p-1.5 text-xs text-primary-foreground hover:bg-primary-hover
                focus:outline-none"
              :aria-label="addEventLabel"
              data-cy="calendar-add-event"
            >
              <Plus class="size-3.5 shrink-0" />
              <span class="sr-only">{{ addEventLabel }}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            class="calendar-add-event-menu"
          >
            <DropdownMenuItem
              v-for="item in modeItems"
              :key="item.id"
              :disabled="item.disabled"
              :class="[
                'flex items-center gap-x-2.5 px-2.5 py-1.5 text-sm',
                item.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
              ]"
              @click="!item.disabled && emit('add-event', item.id)"
            >
              <component
                :is="item.icon"
                class="size-4 shrink-0 text-primary"
              />
              <span class="grow">{{ $props[item.labelKey] }}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>

    <div class="flex flex-wrap items-center justify-between gap-2">
      <h1 class="text-lg text-foreground sm:text-2xl">
        <span class="me-1 font-semibold">{{ headingDate }}</span>
        <span v-if="headingYear">{{ headingYear }}</span>
      </h1>

      <div class="flex -space-x-px">
        <button
          type="button"
          class="border-layer-line bg-layer text-layer-foreground shadow-2xs hover:bg-layer-hover
            focus:bg-layer-focus inline-flex size-[1.875rem] items-center justify-center rounded-s-md border
            text-xs focus:outline-none"
          :aria-label="prevLabel"
          data-cy="calendar-nav-prev"
          @click="emit('go-prev')"
        >
          <svg
            class="size-3.5 shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
        <button
          type="button"
          class="border-layer-line bg-layer text-layer-foreground shadow-2xs hover:bg-layer-hover
            focus:bg-layer-focus inline-flex items-center border px-3 py-1.5 text-xs focus:outline-none"
          data-cy="calendar-nav-today"
          @click="emit('go-today')"
        >
          {{ todayLabel }}
        </button>
        <button
          type="button"
          class="border-layer-line bg-layer text-layer-foreground shadow-2xs hover:bg-layer-hover
            focus:bg-layer-focus inline-flex size-[1.875rem] items-center justify-center rounded-e-md border
            text-xs focus:outline-none"
          :aria-label="nextLabel"
          data-cy="calendar-nav-next"
          @click="emit('go-next')"
        >
          <svg
            class="size-3.5 shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>
